<template>
  <div class="relative">
    <div class="relative flex items-center">
      <!-- Input de recherche -->
      <input v-if="!selectedEntity" v-model="searchQuery" type="text"
        class="w-full p-2 border border-gray-300 rounded-md mb-0 no-margin-bottom"
        :class="{ 'bg-gray-100 cursor-not-allowed': isModelMode }" :placeholder="getPlaceholder" :disabled="isModelMode"
        @input="handleSearch" @focus="handleFocus" @blur="handleBlur" />

      <!-- Affichage de l'entité sélectionnée -->
      <div v-if="selectedEntity"
        class="w-full p-2 border border-gray-300 rounded-md bg-gray-50 flex items-center justify-between">
        <div class="flex-1">
          <div class="font-medium text-gray-900">
            {{ selectedEntity.appellation || selectedEntity.identifier || 'Entité sans nom' }}
            <span v-if="selectedEntity.identifier && selectedEntity.appellation" class="text-gray-500 ml-1">{{
              selectedEntity.identifier }}</span>
          </div>
          <div class="text-sm text-gray-600">
            <template
              v-if="selectedEntity.provenance_location && (selectedEntity.provenance_location.site || selectedEntity.provenance_location.place)">
              <span v-if="selectedEntity.provenance_location.site">{{ selectedEntity.provenance_location.site }}</span>
              <span v-if="selectedEntity.provenance_location.site && selectedEntity.provenance_location.place"> -
              </span>
              <span v-if="selectedEntity.provenance_location.place">{{ selectedEntity.provenance_location.place
              }}</span>
              <span
                v-if="(selectedEntity.provenance_location.site || selectedEntity.provenance_location.place) && (selectedEntity.type || selectedEntity.material)">,
              </span>
            </template>
            <span v-if="selectedEntity.type">{{ selectedEntity.type }}</span>
            <span v-if="selectedEntity.type && selectedEntity.material"> - </span>
            <span v-if="selectedEntity.material">{{ selectedEntity.material }}</span>
          </div>
        </div>

        <!-- Actions pour l'entité sélectionnée -->
        <div class="flex items-center gap-2">
          <!-- Badge public si applicable -->
          <span v-if="selectedEntity.is_public"
            class="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
            <GlobeIcon class="h-3 w-3 mr-1" />
            Public
          </span>

          <!-- Bouton d'édition (seulement pour mes entités) -->
          <button v-if="canEditSelectedEntity && !readOnlyConfig?.isReadOnly" title="Modifier l'entité"
            class="p-2 text-blue-500 hover:text-blue-700" @click="editEntity">
            <PencilIcon class="h-4 w-4" />
          </button>

          <!-- Bouton de réinitialisation -->
          <button v-if="!readOnlyConfig?.isReadOnly" title="Réinitialiser" class="p-2 text-red-500 hover:text-red-700"
            @click="resetAll">
            <XIcon class="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>

    <!-- Dropdown avec sections séparées -->
    <div v-if="showUserEntities && !selectedEntity"
      class="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-96 overflow-y-auto">
      <!-- Bouton créer -->
      <div v-if="!readOnlyConfig?.isReadOnly" title="Créer une nouvelle entité matérielle"
        class="p-3 hover:bg-gray-100 cursor-pointer text-blue-600 border-b border-gray-100 flex items-center gap-2"
        @click="openCreator">
        <PlusIcon class="h-4 w-4" />
        <span>Créer une nouvelle entité matérielle</span>
      </div>

      <!-- Section : Mes entités -->
      <div v-if="myEntities.length > 0" class="section">
        <div class="section-header p-2 bg-blue-50 border-b border-blue-100 flex items-center gap-2">
          <UserIcon class="h-4 w-4 text-blue-600" />
          <span class="text-sm font-medium text-blue-900">Mes entités ({{ myEntities.length }})</span>
        </div>
        <div v-for="entity in myEntities" :key="entity.uuid"
          class="p-3 hover:bg-gray-100 cursor-pointer flex justify-between items-center border-l-3 border-blue-500"
          @click="selectUserEntity(entity)">
          <div class="flex-1">
            <div class="font-medium text-gray-900">
              {{ entity.appellation || entity.identifier || 'Entité sans nom' }}
              <span v-if="entity.identifier && entity.appellation" class="text-gray-500 ml-1">{{ entity.identifier
              }}</span>
            </div>
            <div class="text-sm text-gray-600">
              <template
                v-if="entity.provenance_location && (entity.provenance_location.site || entity.provenance_location.place)">
                <span v-if="entity.provenance_location.site">{{ entity.provenance_location.site }}</span>
                <span v-if="entity.provenance_location.site && entity.provenance_location.place"> - </span>
                <span v-if="entity.provenance_location.place">{{ entity.provenance_location.place }}</span>
                <span
                  v-if="(entity.provenance_location.site || entity.provenance_location.place) && (entity.type || entity.material)">,
                </span>
              </template>
              <span v-if="entity.type">{{ entity.type }}</span>
              <span v-if="entity.type && entity.material"> - </span>
              <span v-if="entity.material">{{ entity.material }}</span>
              <span
                v-if="!entity.type && !entity.material && !(entity.provenance_location && (entity.provenance_location.site || entity.provenance_location.place))">
                Pas d'information disponible
              </span>
            </div>
          </div>
          <div class="flex items-center gap-2 ml-4">
            <button title="Utiliser comme modèle"
              class="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors"
              @click.stop="useAsTemplate(entity)">
              Modèle
            </button>
          </div>
        </div>
      </div>

      <!-- Section : Entités publiques -->
      <div v-if="publicEntities.length > 0" class="section">
        <div class="section-header p-2 bg-emerald-50 border-b border-emerald-100 flex items-center gap-2">
          <GlobeIcon class="h-4 w-4 text-emerald-600" />
          <span class="text-sm font-medium text-emerald-900">Entités publiques ({{ publicEntities.length }})</span>
        </div>
        <div v-for="entity in publicEntities" :key="entity.uuid"
          class="p-3 hover:bg-gray-100 cursor-pointer flex justify-between items-center border-l-3 border-emerald-500"
          @click="selectUserEntity(entity)">
          <div class="flex-1">
            <div class="font-medium text-gray-900 flex items-center gap-2">
              {{ entity.appellation || entity.identifier || 'Entité sans nom' }}
              <span
                class="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                <GlobeIcon class="h-3 w-3 mr-1" />
                Public
              </span>
              <span v-if="entity.identifier && entity.appellation" class="text-gray-500 ml-1">{{ entity.identifier
              }}</span>
            </div>
            <div class="text-sm text-gray-600">
              <template
                v-if="entity.provenance_location && (entity.provenance_location.site || entity.provenance_location.place)">
                <span v-if="entity.provenance_location.site">{{ entity.provenance_location.site }}</span>
                <span v-if="entity.provenance_location.site && entity.provenance_location.place"> - </span>
                <span v-if="entity.provenance_location.place">{{ entity.provenance_location.place }}</span>
                <span
                  v-if="(entity.provenance_location.site || entity.provenance_location.place) && (entity.type || entity.material)">,
                </span>
              </template>
              <span v-if="entity.type">{{ entity.type }}</span>
              <span v-if="entity.type && entity.material"> - </span>
              <span v-if="entity.material">{{ entity.material }}</span>
              <span
                v-if="!entity.type && !entity.material && !(entity.provenance_location && (entity.provenance_location.site || entity.provenance_location.place))">
                Pas d'information disponible
              </span>
            </div>
          </div>
          <div class="flex items-center gap-2 ml-4">
            <button title="Utiliser comme modèle"
              class="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors"
              @click.stop="useAsTemplate(entity)">
              Modèle
            </button>
          </div>
        </div>
      </div>

      <!-- Message si aucune entité -->
      <div v-if="myEntities.length === 0 && publicEntities.length === 0" class="p-4 text-center text-gray-500">
        <div class="flex flex-col items-center gap-2">
          <PackageIcon class="h-8 w-8 text-gray-400" />
          <span>Aucune entité matérielle trouvée</span>
          <span class="text-xs">Créez-en une nouvelle pour commencer</span>
        </div>
      </div>
    </div>

    <!-- Créateur/Éditeur d'entité matérielle -->
    <MaterialEntityCreator v-if="showCreator" :user-id="userId" :show="showCreator" :mode="creatorMode"
      :initial-data="selectedEntityForEdit" @update:show="showCreator = $event"
      @material-entity-created="handleEntityCreated" @material-entity-updated="handleEntityUpdated" />
  </div>
</template>

<script lang="ts">
import MaterialEntityCreator from '@/components/creators/MaterialEntityCreator.vue';
import { useMaterialEntities } from '@/composables/useAssets';
import { GlobeIcon, PackageIcon, PencilIcon, PlusIcon, UserIcon, XIcon } from 'lucide-vue-next';
import { computed, defineComponent, reactive, ref, watch } from 'vue';
import type { MaterialEntity, MaterialEntityCreate } from '../../api/types/entities/materialEntity/types';

export default defineComponent({
  name: 'MaterialEntitySelector',
  components: {
    XIcon,
    PencilIcon,
    UserIcon,
    GlobeIcon,
    PlusIcon,
    PackageIcon,
    MaterialEntityCreator,
  },
  props: {
    userId: {
      type: String,
      required: true
    },
    initialEntityUuid: {
      type: String,
      required: false,
      default: ''
    },
    isModelMode: {
      type: Boolean,
      required: false,
      default: false
    },
    materialEntity: {
      type: Object,
      default: null
    },
    meAppellation: {
      type: String,
      required: false,
      default: ''
    },
    meIdentifier: {
      type: String,
      required: false,
      default: ''
    },
    // Nouveaux props du système de cache
    assetsCacheStore: {
      type: Object,
      default: null
    },
    canEditAsset: {
      type: Function,
      default: null
    },
    isPublicAsset: {
      type: Function,
      default: null
    },
    isMyAsset: {
      type: Function,
      default: null
    },
    readOnlyConfig: {
      type: Object,
      required: false,
      default: () => ({ isReadOnly: false, allowNavigation: true, showEditHints: true })
    }
  },
  emits: ['entity-selected', 'update:me-appellation', 'update:me-identifier', 'entity-reset'],

  setup(props, { emit }) {
    // Utilisation du composable pour le cache avec filtre 'accessible' pour avoir toutes les entités
    const {
      filteredAssets: allEntities,
      isLoading,
      addAsset,
      updateAsset,
      canEditAsset: composableCanEdit
    } = useMaterialEntities({ defaultFilter: 'accessible' });

    // État local
    const searchQuery = ref('');
    const showCreator = ref(false);
    const showUserEntities = ref(false);
    const selectedEntity = ref<MaterialEntity | null>(null);
    const modeEdit = ref(false);

    // Variables pour le modal
    const creatorMode = ref<'create' | 'edit' | 'duplicate'>('create');
    const selectedEntityForEdit = ref<MaterialEntity | undefined>(undefined);

    const entityData = reactive<MaterialEntityCreate>({
      user_id: props.userId,
      appellation: '',
      identifier: '',
      type: '',
      material: '',
      surface_state: '',
      me_link: '',
      material_notes: '',
      scale: '',
      me_owner: '',
      provenance: '',
      provenance_interior_exterior: '',
      provenance_zoning: '',
      provenance_floor: '',
      provenance_orientation: '',
      provenance_nomenclature: '',
      is_public: false
    });

    // Computed pour séparer mes entités des publiques avec recherche
    const myEntities = computed((): MaterialEntity[] => {
      const filtered = allEntities.value.filter(entity => {
        // Mes entités = celles dont je suis propriétaire
        return entity.user_id === props.userId;
      });

      if (!searchQuery.value) return filtered;

      return filtered.filter(entity => {
        const searchableFields = ['appellation', 'identifier', 'type', 'material'];
        return searchableFields.some(field => {
          const value = entity[field as keyof MaterialEntity];
          return typeof value === 'string' && value.toLowerCase().includes(searchQuery.value.toLowerCase());
        });
      });
    });

    const publicEntities = computed((): MaterialEntity[] => {
      const filtered = allEntities.value.filter(entity => {
        // Entités publiques = celles qui sont publiques ET qui ne sont pas à moi
        return entity.is_public && entity.user_id !== props.userId;
      });

      if (!searchQuery.value) return filtered;

      return filtered.filter(entity => {
        const searchableFields = ['appellation', 'identifier', 'type', 'material'];
        return searchableFields.some(field => {
          const value = entity[field as keyof MaterialEntity];
          return typeof value === 'string' && value.toLowerCase().includes(searchQuery.value.toLowerCase());
        });
      });
    });

    // Helper pour vérifier si on peut éditer l'entité sélectionnée
    const canEditSelectedEntity = computed((): boolean => {
      if (!selectedEntity.value) return false;

      // Utiliser les helpers du composable ou ceux passés en props
      const canEditFn = props.canEditAsset || composableCanEdit;
      return canEditFn(selectedEntity.value);
    });



    // Watcher pour props.materialEntity
    watch(() => props.materialEntity, (newEntity) => {
      if (newEntity) {
        selectedEntity.value = newEntity as MaterialEntity;
      }
    }, { immediate: true });

    // Watchers pour les props meAppellation et meIdentifier
    watch(() => props.meAppellation, (newValue) => {
      if (selectedEntity.value && selectedEntity.value.appellation !== newValue) {
        emit('update:me-appellation', selectedEntity.value.appellation);
      }
    });

    watch(() => props.meIdentifier, (newValue) => {
      if (selectedEntity.value && selectedEntity.value.identifier !== newValue) {
        emit('update:me-identifier', selectedEntity.value.identifier);
      }
    });

    const handleFocus = (): void => {
      if (props.isModelMode || selectedEntity.value) return;
      showUserEntities.value = true;
    };

    const handleSearch = (): void => {
      if (props.isModelMode || selectedEntity.value) return;
      showUserEntities.value = true;
    };

    const selectUserEntity = (entity: MaterialEntity): void => {
      selectedEntity.value = entity;
      searchQuery.value = entity.appellation || entity.identifier || 'Entité sans nom';
      emit('entity-selected', { uuid: entity.uuid, entity });
      showUserEntities.value = false;
    };

    const resetSearch = (): void => {
      selectedEntity.value = null;
      searchQuery.value = '';
      showUserEntities.value = false;
      modeEdit.value = false;
      showCreator.value = false;
    };

    const resetAll = (): void => {
      resetSearch();
      emit('entity-reset');
    };

    const openCreator = (): void => {
      creatorMode.value = 'create';
      selectedEntityForEdit.value = undefined;
      showCreator.value = true;
      showUserEntities.value = false;
    };

    const editEntity = (): void => {
      if (!selectedEntity.value || !canEditSelectedEntity.value) {
        return;
      }
      creatorMode.value = 'edit';
      selectedEntityForEdit.value = selectedEntity.value;
      showCreator.value = true;
      showUserEntities.value = false;
    };

    const handleEntityCreated = (entity: MaterialEntity): void => {
      // Ajouter au cache
      addAsset(entity);

      // Sélectionner l'entité créée
      selectUserEntity(entity);

      // Fermer le modal
      showCreator.value = false;
    };

    const handleEntityUpdated = (entity: MaterialEntity): void => {
      // Mettre à jour le cache
      updateAsset(entity);

      // Mettre à jour l'entité sélectionnée
      selectedEntity.value = entity;

      // Fermer le modal
      showCreator.value = false;
    };

    const useAsTemplate = (entity: MaterialEntity): void => {
      creatorMode.value = 'duplicate';
      selectedEntityForEdit.value = entity;
      showCreator.value = true;
      showUserEntities.value = false;
    };

    const handleBlur = (): void => {
      // On utilise setTimeout pour permettre aux clics sur la liste d'être traités avant
      setTimeout(() => {
        showUserEntities.value = false;
      }, 200);
    };



    const getPlaceholder = computed((): string => {
      if (props.isModelMode) {
        return selectedEntity.value ? selectedEntity.value.appellation : 'Mode modèle activé, sélection impossible';
      }
      else if (selectedEntity.value) {
        return selectedEntity.value.appellation || selectedEntity.value.identifier
          ? [selectedEntity.value.appellation, selectedEntity.value.identifier].filter(Boolean).join(' - ')
          : 'Entité sans nom';
      }
      return 'Rechercher une entité matérielle...';
    });

    return {
      searchQuery,
      showCreator,
      showUserEntities,
      selectedEntity,
      entityData,
      modeEdit,
      myEntities,
      publicEntities,
      canEditSelectedEntity,
      isLoading,
      handleFocus,
      handleSearch,
      handleBlur,
      selectUserEntity,
      resetSearch,
      resetAll,
      openCreator,
      editEntity,
      handleEntityCreated,
      handleEntityUpdated,
      useAsTemplate,
      creatorMode,
      selectedEntityForEdit,
      getPlaceholder
    };
  }
});
</script>

<style scoped>
.border-l-3 {
  border-left-width: 3px;
}

.section-header {
  position: sticky;
  top: 0;
  z-index: 10;
}
</style>